import React, { useState, useEffect } from 'react';
import {
    Button,
    Card,
    Form,
    Input,
    Select,
    Switch,
    Breadcrumb,
    Row,
    Col,
    Typography,
    message,
    Modal,
    Tooltip
} from 'antd';
import {
    UserOutlined,
    MailOutlined,
    TeamOutlined,
    IdcardOutlined,
    CopyOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/api';
import { usePageTitle } from '../context/PageTitleContext';
const { Text } = Typography;

const { Option } = Select;
const { TextArea } = Input;

const UserCreate = () => {
    const navigate = useNavigate();
    const { setPageTitle, setRightContent } = usePageTitle();
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [newUserData, setNewUserData] = useState(null);
    const [credentialsModalVisible, setCredentialsModalVisible] = useState(false);

    useEffect(() => {
        setPageTitle('Создание пользователя');

        setRightContent(
            <Button
                type="primary"
                size="large"
                loading={submitting}
                onClick={() => form.submit()}
            >
                Создать
            </Button>
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent, submitting, form]);

    const handleSubmit = async (values) => {
        try {
            setSubmitting(true);
            const response = await userService.createUser(values);

            if (response.status === 'success') {
                messageApi.success('Пользователь успешно создан');

                // Сохраняем данные нового пользователя и показываем модальное окно
                setNewUserData(response.data);
                setCredentialsModalVisible(true);
            } else {
                messageApi.error(response.message || 'Не удалось создать пользователя');
            }
        } catch (error) {
            console.error('Error creating user:', error);
            messageApi.error(error.response?.data?.message || 'Ошибка при создании пользователя');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopyText = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                messageApi.success('Скопировано в буфер обмена');
            })
            .catch(() => {
                messageApi.error('Не удалось скопировать текст');
            });
    };

    const getFullCredentialsText = () => {
        if (!newUserData) return '';
        return `Логин: ${newUserData.username}
Временный пароль: ${newUserData.temporary_password}
Текущая почта: ${newUserData.email}
Перед тем как авторизоваться в других сервисах, войдите в сервис ID по временному паролю и смените его на постоянный.`;
    };

    const goToUserDetails = () => {
        setCredentialsModalVisible(false);
        navigate(`/users/${newUserData.id}`);
    };

    return (
        <>
            {contextHolder}
            <Breadcrumb
                items={[
                    { title: <a onClick={() => navigate('/users')}>Пользователи</a> },
                    { title: "Создание пользователя" }
                ]}
                style={{ marginBottom: 16 }}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    is_active: true,
                    role_id: 3 // Пользователь (3) по умолчанию
                }}
                size="large"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Card
                            title={
                                <span>
                                    <UserOutlined style={{ marginRight: 8 }} />
                                    Учетные данные
                                </span>
                            }
                            style={{ marginBottom: 16 }}
                        >
                            <Form.Item
                                name="username"
                                label="Логин"
                                rules={[
                                    { required: true, message: 'Пожалуйста, введите логин' },
                                    { min: 3, message: 'Логин должен содержать минимум 3 символа' }
                                ]}
                            >
                                <Input prefix={<UserOutlined />} placeholder="Введите логин" />
                            </Form.Item>

                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[
                                    { required: true, message: 'Пожалуйста, введите email' },
                                    { type: 'email', message: 'Пожалуйста, введите корректный email' }
                                ]}
                            >
                                <Input prefix={<MailOutlined />} placeholder="Введите email" />
                            </Form.Item>

                            <p className="ant-form-item-extra">
                                После создания пользователя будет сгенерирован временный пароль
                            </p>
                        </Card>
                    </Col>

                    <Col span={12}>
                        <Card
                            title={
                                <span>
                                    <IdcardOutlined style={{ marginRight: 8 }} />
                                    Личные данные
                                </span>
                            }
                            style={{ marginBottom: 16 }}
                        >
                            <Form.Item
                                name="first_name"
                                label="Имя"
                            >
                                <Input placeholder="Введите имя" />
                            </Form.Item>

                            <Form.Item
                                name="last_name"
                                label="Фамилия"
                            >
                                <Input placeholder="Введите фамилию" />
                            </Form.Item>

                            <Form.Item
                                name="department"
                                label="Отдел"
                            >
                                <Input prefix={<TeamOutlined />} placeholder="Введите отдел" />
                            </Form.Item>

                            <Form.Item
                                name="position"
                                label="Должность"
                            >
                                <Input prefix={<IdcardOutlined />} placeholder="Введите должность" />
                            </Form.Item>
                        </Card>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Card
                            title="Настройки доступа"
                            style={{ marginBottom: 16 }}
                        >
                            <Form.Item
                                name="role_id"
                                label="Роль"
                                rules={[{ required: true, message: 'Пожалуйста, выберите роль' }]}
                            >
                                <Select placeholder="Выберите роль">
                                    <Option value={1}>Администратор</Option>
                                    <Option value={3}>Пользователь</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="is_active"
                                label="Активен"
                                valuePropName="checked"
                            >
                                <Switch checkedChildren="Да" unCheckedChildren="Нет" />
                            </Form.Item>
                        </Card>
                    </Col>
                </Row>
            </Form>

            <Modal
                title="Пользователь успешно создан"
                open={credentialsModalVisible}
                onCancel={goToUserDetails}
                footer={[
                    <Button key="details" type="primary" onClick={goToUserDetails}>
                        Перейти к пользователю
                    </Button>
                ]}
            >
                {newUserData && (
                    <>
                        <p>Отправьте следующие данные пользователю:</p>

                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Временный пароль:</Text>
                            <div style={{ display: 'flex', marginTop: 8 }}>
                                <Input
                                    readOnly
                                    value={newUserData.temporary_password}
                                    style={{ marginRight: 8 }}
                                />
                                <Tooltip title="Скопировать пароль">
                                    <Button
                                        icon={<CopyOutlined />}
                                        onClick={() => handleCopyText(newUserData.temporary_password)}
                                    />
                                </Tooltip>
                            </div>
                        </div>

                        <div style={{ marginTop: 16 }}>
                            <Text strong>Полные данные для входа:</Text>
                            <div style={{ display: 'flex', marginTop: 8 }}>
                                <TextArea
                                    readOnly
                                    rows={4}
                                    value={getFullCredentialsText()}
                                    style={{ marginRight: 8 }}
                                />
                                <Tooltip title="Скопировать данные">
                                    <Button
                                        icon={<CopyOutlined />}
                                        onClick={() => handleCopyText(getFullCredentialsText())}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    </>
                )}
            </Modal>
        </>
    );
};

export default UserCreate;